import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { driversForShift } from './mockRoster';
import { slotsForDriver } from './mockAssignments';
import { C } from './styles';
import { useRunQueue, DND } from './runqueue/RunQueueContext';
import { rowMatchesQuery } from './searchBar';

const ROW_H = 36;
const HEADER_H = 44;
const SLOT_COUNT = 8;

// Color thresholds for the time-between-runs cell:
//   < 50 min       → red    (too tight)
//   50 – 75 min    → yellow (acceptable, ~1hr is the target)
//   > 75 min       → green  (comfortable cushion)
function gapColor(mins) {
  if (mins == null) return null;
  if (mins < 50)  return C.hot;
  if (mins <= 75) return C.warn;
  return C.ok;
}

function fmtGap(mins) {
  if (mins == null) return '';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
}

function parseEtaMins(eta) {
  if (!eta) return null;
  const m = String(eta).match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
}

function hexToRgba(hex, alpha) {
  if (!hex) return null;
  const m = hex.replace('#', '').match(/.{2}/g);
  if (!m || m.length < 3) return null;
  const [r, g, b] = m.map(x => parseInt(x, 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function AssignmentGrid({ shift = 'AM', driverTeamColor = new Map(), searchQuery = '' }) {
  const drivers = useMemo(() => driversForShift(shift), [shift]);
  const { assignments, snap, assignRunToDriver, moveSlot, dragCandidates } = useRunQueue();

  // Cluster team drivers together (by team color), then everyone else.
  const orderedDrivers = useMemo(() => {
    const inTeam = [];
    const noTeam = [];
    drivers.forEach(d => {
      if (driverTeamColor.has(d.id)) inTeam.push(d);
      else noTeam.push(d);
    });
    inTeam.sort((a, b) => {
      const ca = driverTeamColor.get(a.id);
      const cb = driverTeamColor.get(b.id);
      if (ca === cb) return 0;
      return ca < cb ? -1 : 1;
    });
    return [...inTeam, ...noTeam];
  }, [drivers, driverTeamColor]);

  const driverRows = useMemo(() => orderedDrivers.map((driver) => {
    const slots = slotsForDriver(driver.id, assignments);
    // Per-driver candidate state for the active drag (null when not dragging).
    const candidateFit = dragCandidates ? dragCandidates.get(driver.id) || null : null;
    // First empty slot for the candidate highlight to land on.
    let candidateSlot = null;
    if (candidateFit) {
      candidateSlot = slots.findIndex(s => s === null);
      if (candidateSlot === -1) candidateSlot = null;
    }
    return {
      driver,
      slots,
      teamColor: driverTeamColor.get(driver.id) || null,
      candidateFit,    // 'green' | 'team' | null
      candidateSlot,   // slot index (0–7) or null
    };
  }), [orderedDrivers, driverTeamColor, assignments, dragCandidates]);

  // Filter by search query — empty = pass-through, otherwise rowMatchesQuery.
  const filteredRows = useMemo(
    () => driverRows.filter(({ driver, slots }) => rowMatchesQuery(searchQuery, driver, slots)),
    [driverRows, searchQuery]
  );

  const W = {
    num: 42,
    name: 180,
    shift: 70,
    flight: 64,
    eta: 50,
    role: 50,
    gap: 56,
  };

  const TOTAL_W = W.num + W.name + W.shift
    + SLOT_COUNT * (W.flight + W.eta + W.role)
    + (SLOT_COUNT - 1) * W.gap;

  // Build column hover CSS — uses :has() so no React state, no re-renders.
  // For each unique column index, "if any cell in this column is hovered,
  // tint every cell in this column."
  const TOTAL_COLS = 3 + SLOT_COUNT * 3 + (SLOT_COUNT - 1); // pinned + slot-trios + gaps
  const columnHoverCss = useMemo(() => {
    const rules = {};
    // Soft neutral wash — easy on the eyes, doesn't fight team colors
    const tint = 'rgba(255, 255, 255, 0.05)';
    for (let i = 0; i < TOTAL_COLS; i++) {
      rules[`&:has(.cell-c${i}:hover) .cell-c${i}`] = { backgroundColor: tint };
    }
    return rules;
  }, [TOTAL_COLS]);

  return (
    <Box sx={{
      flex: 1,
      overflow: 'auto',
      position: 'relative',
      backgroundColor: C.surface,
      backdropFilter: C.blur,
      WebkitBackdropFilter: C.blur,
      '&::-webkit-scrollbar': { height: 8, width: 8 },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        borderRadius: 4,
      },
      ...columnHoverCss,
    }}>
      <Box sx={{ width: TOTAL_W, minWidth: '100%' }}>

        {/* Header (sticky top) — solid bg so scrolling rows can't
            bleed through the column labels. */}
        <Box sx={{
          display: 'flex',
          height: HEADER_H,
          position: 'sticky',
          top: 0,
          zIndex: 5,
          backgroundColor: C.surfaceHiSolid,
          borderBottom: `2px solid ${C.borderStrong}`,
          // Same 4px left offset as body rows — keeps headers aligned
          // whether or not a row has a team-color bar.
          borderLeft: '4px solid transparent',
        }}>
          <HeaderCell w={W.num}   col={0}  sticky left={0}>#</HeaderCell>
          <HeaderCell w={W.name}  col={1}  sticky left={W.num} align="left">Agent</HeaderCell>
          <HeaderCell w={W.shift} col={2}  sticky left={W.num + W.name} borderRight>Shift</HeaderCell>

          {Array.from({ length: SLOT_COUNT }).map((_, i) => {
            // Each slot is 3 cols (flight, eta, role) + 1 gap col after (except last)
            const baseCol = 3 + i * 4;
            return (
              <React.Fragment key={i}>
                <HeaderCell w={W.flight} col={baseCol}    >F{i + 1}</HeaderCell>
                <HeaderCell w={W.eta}    col={baseCol + 1}>ETA</HeaderCell>
                <HeaderCell w={W.role}   col={baseCol + 2} borderRight>Role</HeaderCell>
                {i < SLOT_COUNT - 1 && (
                  <HeaderCell w={W.gap} col={baseCol + 3} muted>↔</HeaderCell>
                )}
              </React.Fragment>
            );
          })}
        </Box>

        {/* Body */}
        {filteredRows.map(({ driver, slots, teamColor, candidateFit, candidateSlot }, rowIdx) => (
          <DriverRow
            key={driver.id}
            driver={driver}
            slots={slots}
            teamColor={teamColor}
            rowIdx={rowIdx}
            W={W}
            snap={snap}
            onAssign={assignRunToDriver}
            onMove={moveSlot}
            candidateFit={candidateFit}
            candidateSlot={candidateSlot}
          />
        ))}
      </Box>
    </Box>
  );
}

// Pinned columns can't be translucent — content scrolling underneath
// bleeds through and the driver name becomes unreadable. Solid values
// here keep the # / Agent / Shift cells legible at any scroll
// position. Team identity is preserved by the 4px team-colored left
// border on the row.
const STICKY_BG       = C.surfaceSolid;     // base #18191C
const STICKY_HOVER_BG = C.surfaceHiSolid;   // base #23252A

// Memoized row — only re-renders if its inputs change. Combined with CSS-only
// hover, this keeps interactions instant even at 110 rows.
const DriverRow = React.memo(function DriverRow({
  driver, slots, teamColor, rowIdx, W, snap, onAssign, onMove,
  candidateFit, candidateSlot,
}) {
  const [dragOver, setDragOver] = useState(false);
  const stripeBg = rowIdx % 2 === 0
    ? 'transparent'
    : 'rgba(255, 255, 255, 0.02)';
  const baseBg = teamColor ? hexToRgba(teamColor, 0.16) : stripeBg;
  // Row hover stays subtle / neutral by default; team rows hover into a
  // slightly stronger version of their own color (so the team identity
  // is preserved on hover).
  const hoverBg = teamColor ? hexToRgba(teamColor, 0.26) : 'rgba(255, 255, 255, 0.06)';

  // Drop target: accepts runs from the queue OR runs being moved between
  // drivers. The row decides which empty slot to drop into.
  const handleDragOver = (e) => {
    const types = e.dataTransfer.types;
    if (types.includes(DND.RUN_FROM_QUEUE) || types.includes(DND.RUN_FROM_GRID)) {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      if (!dragOver) setDragOver(true);
    }
  };
  const handleDragLeave = (e) => {
    // Only clear if leaving the row itself (not entering a child).
    if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const queueRunId = e.dataTransfer.getData(DND.RUN_FROM_QUEUE);
    if (queueRunId) {
      onAssign(queueRunId, driver.id);
      return;
    }
    const gridPayload = e.dataTransfer.getData(DND.RUN_FROM_GRID);
    if (gridPayload) {
      const [fromDriverId, fromSlotStr] = gridPayload.split('|');
      onMove(fromDriverId, parseInt(fromSlotStr, 10), driver.id);
    }
  };

  return (
    <Box
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      sx={{
        display: 'flex',
        height: ROW_H,
        borderBottom: `1px solid ${C.border}`,
        borderLeft: teamColor ? `4px solid ${teamColor}` : '4px solid transparent',
        backgroundColor: dragOver
          ? `${C.accentBg} !important`
          : baseBg,
        boxShadow: dragOver ? `inset 0 0 0 2px ${C.accent}` : 'none',
        fontSize: '0.72rem',
        color: C.textPrimary,
        transition: 'background-color 80ms, box-shadow 80ms',
        // Pure CSS hover — no React state, no re-renders
        '&:hover': {
          backgroundColor: hoverBg,
          // Pinned cells use a solid hover (not the translucent row
          // tint) so they stay legible while content scrolls behind.
          '& .pinned-cell': { backgroundColor: STICKY_HOVER_BG },
        },
      }}
    >
      {/* Pinned: # */}
      <DataCell w={W.num} col={0} sticky left={0} bg={STICKY_BG}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: C.textMuted }}>
          {rowIdx + 1}
        </Typography>
      </DataCell>

      {/* Pinned: Agent */}
      <DataCell w={W.name} col={1} sticky left={W.num} bg={STICKY_BG} align="left" px={1}>
        <Typography noWrap sx={{ fontSize: '0.78rem', color: C.textPrimary, fontWeight: 600 }}>
          {driver.displayName}
          {' '}
          <Box component="span" sx={{ color: C.textMuted, fontWeight: 400, fontSize: '0.7rem' }}>
            ({driver.seniority})
          </Box>
        </Typography>
      </DataCell>

      {/* Pinned: Shift */}
      <DataCell w={W.shift} col={2} sticky left={W.num + W.name} bg={STICKY_BG} borderRight>
        <Typography sx={{ fontSize: '0.65rem', color: C.textSecondary, fontWeight: 600 }}>
          {driver.shiftCode}
        </Typography>
      </DataCell>

      {/* 8 slot groups + 7 gaps */}
      {slots.map((run, i) => {
        const next = slots[i + 1];
        const gapMins = (run && next)
          ? (parseEtaMins(next.inboundEta) - parseEtaMins(run.inboundEta) + 1440) % 1440
          : null;
        const baseCol = 3 + i * 4;
        const justSnapped = snap && snap.driverId === driver.id && snap.slot === i;
        // Candidate highlight on the next-empty slot for an active drag.
        const isCandidateSlot = candidateFit && candidateSlot === i;
        return (
          <React.Fragment key={i}>
            <FlightCell w={W.flight} col={baseCol}     run={run} driverId={driver.id} slot={i} snapped={justSnapped} candidateFit={isCandidateSlot ? candidateFit : null} />
            <EtaCell    w={W.eta}    col={baseCol + 1} run={run} snapped={justSnapped} candidateFit={isCandidateSlot ? candidateFit : null} />
            <RoleCell   w={W.role}   col={baseCol + 2} run={run} snapped={justSnapped} candidateFit={isCandidateSlot ? candidateFit : null} borderRight />
            {i < SLOT_COUNT - 1 && (
              <GapCell w={W.gap} col={baseCol + 3} mins={gapMins} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
});

// ── Cell primitives ────────────────────────────────────────────────

function HeaderCell({ w, col, sticky, left, borderRight, align = 'center', muted, children }) {
  return (
    <Box
      className={`cell-c${col}`}
      sx={{
        width: w,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'left' ? 'flex-start' : 'center',
        px: align === 'left' ? 1 : 0,
        borderRight: borderRight ? `2px solid ${C.borderStrong}` : `1px solid ${C.divider}`,
        ...(sticky && {
          position: 'sticky',
          left,
          zIndex: 6,
          // Solid bg so the rows scrolling under the pinned header
          // don't bleed through and obscure the label.
          backgroundColor: C.surfaceHiSolid,
        }),
      }}
    >
      <Typography sx={{
        fontSize: '0.78rem',
        fontWeight: 'bold',
        color: muted ? C.textMuted : C.textSecondary,
        lineHeight: 1,
      }}>
        {children}
      </Typography>
    </Box>
  );
}

function DataCell({
  w, col, sticky, left, bg, borderRight, align = 'center', px = 0,
  draggable, onDragStart, snapped, candidateFit, children,
}) {
  // Candidate-fit wash while a queue tile is being dragged.
  //   'team'  → strong green (whole team can take this inbound's runs)
  //   'green' → standard green (comfortable solo fit)
  //   'red'   → red wash (driver can't take this run — wrong shift,
  //             no slot, or would create a red gap)
  const candidateBg = candidateFit === 'team'
    ? 'rgba(76, 175, 80, 0.35)'
    : candidateFit === 'green'
    ? 'rgba(76, 175, 80, 0.22)'
    : candidateFit === 'red'
    ? 'rgba(255, 0, 0, 0.22)'
    : null;
  const candidateBorderColor = candidateFit === 'red'
    ? 'rgba(255, 0, 0, 0.5)'
    : 'rgba(76, 175, 80, 0.4)';
  const candidateBorderColorPulse = candidateFit === 'red'
    ? 'rgba(255, 0, 0, 0.85)'
    : 'rgba(76, 175, 80, 0.75)';
  return (
    <Box
      className={`cell-c${col}${sticky ? ' pinned-cell' : ''}`}
      draggable={draggable}
      onDragStart={onDragStart}
      sx={{
        width: w,
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: align === 'left' ? 'flex-start' : 'center',
        px,
        borderRight: borderRight ? `2px solid ${C.borderStrong}` : `1px solid ${C.divider}`,
        ...(sticky
          ? { position: 'sticky', left, zIndex: 4, backgroundColor: candidateBg || bg }
          : (candidateBg ? { backgroundColor: candidateBg } : (bg ? { backgroundColor: bg } : {}))),
        ...(draggable && {
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
        }),
        // Snap-in flash when a run was just dropped here. Brief gold
        // wash + subtle scale settle.
        ...(snapped && {
          animation: 'cellSnapIn 600ms cubic-bezier(0.2, 0.8, 0.2, 1)',
          '@keyframes cellSnapIn': {
            '0%':   { backgroundColor: 'rgba(249, 182, 18, 0.55)', transform: 'scale(1.08)' },
            '60%':  { backgroundColor: 'rgba(249, 182, 18, 0.25)', transform: 'scale(1)' },
            '100%': { backgroundColor: 'transparent', transform: 'scale(1)' },
          },
        }),
        // Gentle pulse on the candidate cells so they're noticeable
        // without being obnoxious during the drag. Color matches the
        // candidate fit (green for good, red for "can't take").
        ...(candidateBg && {
          animation: 'cellCandidatePulse 1.4s ease-in-out infinite',
          '@keyframes cellCandidatePulse': {
            '0%, 100%': { boxShadow: `inset 0 0 0 1px ${candidateBorderColor}` },
            '50%':      { boxShadow: `inset 0 0 0 2px ${candidateBorderColorPulse}` },
          },
        }),
      }}
    >
      {children}
    </Box>
  );
}

function FlightCell({ w, col, run, driverId, slot, snapped, candidateFit }) {
  if (!run) return <DataCell w={w} col={col} candidateFit={candidateFit} />;
  const num = (run.inboundFlightNumber || '').replace(/^WN\s*/, '');
  const handleDragStart = (e) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData(DND.RUN_FROM_GRID, `${driverId}|${slot}`);
    e.dataTransfer.setData('text/plain', `${driverId}|${slot}`);
  };
  return (
    <DataCell w={w} col={col} snapped={snapped} draggable onDragStart={handleDragStart}>
      <Typography sx={{ fontSize: '0.72rem', color: C.textPrimary, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
        {num}
      </Typography>
    </DataCell>
  );
}

function EtaCell({ w, col, run, snapped, candidateFit }) {
  if (!run) return <DataCell w={w} col={col} candidateFit={candidateFit} />;
  return (
    <DataCell w={w} col={col} snapped={snapped}>
      <Typography sx={{ fontSize: '0.7rem', color: C.textSecondary, fontVariantNumeric: 'tabular-nums' }}>
        {run.inboundEta}
      </Typography>
    </DataCell>
  );
}

function RoleCell({ w, col, run, borderRight, snapped, candidateFit }) {
  if (!run) return <DataCell w={w} col={col} borderRight={borderRight} candidateFit={candidateFit} />;
  // FWD/AFT get colored chips; "Drv N" / "Solo" stay subdued.
  let chipColor = null;
  if (run.splitType === 'FWD')      chipColor = C.fwd;
  else if (run.splitType === 'AFT') chipColor = C.aft;
  return (
    <DataCell w={w} col={col} borderRight={borderRight} snapped={snapped}>
      <Typography sx={{
        fontSize: '0.62rem',
        color: chipColor || C.textSecondary,
        fontWeight: 700,
        letterSpacing: 0.3,
      }}>
        {run.slotLabel.replace('Drv ', 'D')}
      </Typography>
    </DataCell>
  );
}

function GapCell({ w, col, mins }) {
  if (mins == null) return <DataCell w={w} col={col} />;
  const color = gapColor(mins);
  return (
    <DataCell w={w} col={col} bg={hexToRgba(color, 0.18)}>
      <Typography sx={{
        fontSize: '0.62rem',
        fontWeight: 700,
        color,
        fontVariantNumeric: 'tabular-nums',
      }}>
        {fmtGap(mins)}
      </Typography>
    </DataCell>
  );
}

export default AssignmentGrid;
